import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, UserCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { clinicianAPI, PublicClinician, PaginatedClinicianList } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import { apiErrorMessage } from '../../utils/apiError';

const ClinicianDirectory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();

  const [clinicians, setClinicians] = useState<PublicClinician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 12; // 3x4 grid on desktop

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [specialtyFilter, setSpecialtyFilter] = useState(searchParams.get('specialty') || '');
  const [specialties, setSpecialties] = useState<string[]>([]);

  // Fetch clinicians
  const fetchClinicians = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data: PaginatedClinicianList = await clinicianAPI.listClinicians(
        page,
        limit,
        specialtyFilter || undefined,
        debouncedSearch || undefined
      );

      setClinicians(data.items);
      setTotalPages(data.pages);
      setTotalCount(data.total);

      // Extract unique specialties from all clinicians (first page load)
      if (specialties.length === 0 && data.items.length > 0) {
        const specialtySet = new Set(data.items.map(c => c.specialty));
        const uniqueSpecialties = Array.from(specialtySet).filter(Boolean).sort();
        setSpecialties(uniqueSpecialties);
      }
    } catch (err: any) {
      console.error('Error fetching clinicians:', err);
      setError(apiErrorMessage(err, 'Failed to load clinicians'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, specialtyFilter, specialties.length, debouncedSearch]);

  useEffect(() => {
    fetchClinicians();
  }, [fetchClinicians]);

  // Debounce the search box into the server query, resetting to page 1 on change.
  // Both setState calls are batched, so this triggers a single refetch.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (specialtyFilter) params.set('specialty', specialtyFilter);
    if (searchTerm) params.set('search', searchTerm);
    setSearchParams(params);
  }, [page, specialtyFilter, searchTerm, setSearchParams]);

  // Search (across name/specialty/bio/tags) and A→Z-by-first-name ordering are
  // now handled server-side (see listClinicians `search`), so results span ALL
  // clinicians — not just the current page — and we render them as returned.
  const filteredClinicians = clinicians;

  // Handle specialty filter change
  const handleSpecialtyChange = (specialty: string) => {
    setSpecialtyFilter(specialty);
    setPage(1); // Reset to first page
  };

  // Get back link based on auth state. Unauthenticated visitors are prospective
  // clients, so "back" returns them to the public client home (/clients) — the
  // top of the client funnel — rather than the login wall.
  const getBackLink = () => {
    if (!isAuthenticated) return '/clients';
    if (user?.role === 'patient') return '/patient';
    if (user?.role === 'member') return '/member';
    if (user?.role === 'admin') return '/admin';
    return '/';
  };

  return (
    <div className="min-h-screen bg-clinic-surface">
      {/* Header */}
      <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <Link to={getBackLink()} className="mr-3 sm:mr-4 flex-shrink-0 text-clinic-muted hover:text-clinic-primary transition-colors">
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <Link to="/clients" className="flex items-center min-w-0" aria-label="Clinic Studios">
                {/* Enlarged per Nitika's directory notes: "enlarge logo's height
                    and overall size... so it looks more clear on screen". */}
                <img
                  src="/logos/logo-primary.png"
                  alt="Clinic Studios"
                  className="h-12 sm:h-14 w-auto flex-shrink-0"
                />
              </Link>
            </div>
            {!isAuthenticated && (
              <Button
                as={Link}
                to="/login?redirect=/clinicians"
                size="sm"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page title — above the search bar (per Nitika) */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-clinic-dark font-body">Find a Clinician</h1>
          <p className="text-clinic-muted mt-1">Browse our directory of healthcare professionals</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-card shadow-clinic p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-clinic-muted" />
              <input
                type="text"
                placeholder="Search by name or specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-clinic-primary-lighter rounded-button focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary text-clinic-dark placeholder-clinic-muted transition-colors"
              />
            </div>

            {/* Specialty Filter */}
            <div className="md:w-64">
              <select
                value={specialtyFilter}
                onChange={(e) => handleSpecialtyChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-clinic-primary-lighter rounded-button focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary text-clinic-dark bg-white transition-colors"
              >
                <option value="">All Specialties</option>
                {specialties.map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-clinic-muted">
            {loading ? (
              'Loading...'
            ) : (
              `Showing ${filteredClinicians.length} of ${totalCount} clinician${totalCount !== 1 ? 's' : ''}`
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-clinic-accent/10 border border-clinic-accent/30 rounded-card p-6 text-center">
            <p className="text-clinic-accent">{error}</p>
            <button
              onClick={fetchClinicians}
              className="mt-4 text-sm text-clinic-accent hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredClinicians.length === 0 && (
          <div className="bg-white rounded-card shadow-clinic p-12 text-center">
            <UserCircleIcon className="mx-auto h-16 w-16 text-clinic-muted" />
            <h3 className="mt-4 text-lg font-semibold text-clinic-dark font-body">No clinicians found</h3>
            <p className="mt-2 text-clinic-muted">
              {searchTerm || specialtyFilter
                ? 'Try adjusting your search filters'
                : 'No clinicians are currently available'}
            </p>
            {(searchTerm || specialtyFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSpecialtyFilter('');
                  setPage(1);
                }}
                className="mt-4 text-clinic-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Clinician Grid */}
        {!loading && !error && filteredClinicians.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredClinicians.map((clinician) => (
                <Link
                  key={clinician.id}
                  to={`/clinicians/${clinician.booking_slug}`}
                  className="group bg-white rounded-card shadow-clinic-sm border border-clinic-primary-lighter p-6 hover:shadow-clinic hover:border-clinic-primary transition-all"
                >
                  {/* Photo */}
                  <div className="flex justify-center mb-4">
                    {clinician.profile_photo_url ? (
                      <img
                        src={clinician.profile_photo_url}
                        alt={`${clinician.first_name} ${clinician.last_name}`}
                        className="w-24 h-24 rounded-full object-cover ring-4 ring-clinic-primary-lighter group-hover:ring-clinic-primary transition-colors"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-clinic-primary to-clinic-primary-dark flex items-center justify-center ring-4 ring-clinic-primary-lighter group-hover:ring-clinic-primary transition-colors">
                        <span className="text-3xl font-semibold text-clinic-cream">
                          {clinician.first_name[0]}{clinician.last_name[0]}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-clinic-dark font-body group-hover:text-clinic-primary transition-colors">
                      {clinician.first_name} {clinician.last_name}
                    </h3>
                    <p className="text-sm text-clinic-primary font-medium mt-1">
                      {clinician.specialty}
                    </p>
                    {clinician.specialties && clinician.specialties.length > 0 && (
                      <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                        {clinician.specialties.slice(0, 3).map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded-full bg-clinic-primary/10 text-clinic-primary-dark text-xs px-2.5 py-0.5">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-clinic-primary/10 text-clinic-primary group-hover:bg-clinic-primary group-hover:text-clinic-cream transition-colors">
                        View Profile
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-clinic-primary-lighter rounded-button text-sm font-medium text-clinic-dark hover:bg-clinic-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span className="px-2 text-clinic-muted">...</span>
                        )}
                        <button
                          onClick={() => setPage(p)}
                          className={`w-10 h-10 rounded-button text-sm font-medium transition-colors ${
                            page === p
                              ? 'bg-clinic-primary text-clinic-cream'
                              : 'border border-clinic-primary-lighter text-clinic-dark hover:bg-clinic-cream'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-clinic-primary-lighter rounded-button text-sm font-medium text-clinic-dark hover:bg-clinic-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-clinic-primary-lighter mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img
                src="/logos/logo-icon.png"
                alt="Clinic Studios"
                className="h-8 w-auto opacity-60"
              />
              <p className="ml-4 text-sm text-clinic-muted">
                &copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClinicianDirectory;
